<!--
An input on which all vee validation rules can be applied throught the "rules" prop.
See vee-validationrules.ts for list of available rules

Example use : 
 <FileWithValidation
    rules="required"
    :tooltip="$t('input-email-tooltip')"
    :label="$t('input-email')"
    placeholder="e.g. EXAMPLE VALUE"
    v-model="email"
  />
-->
<template>
  <ValidationProvider
    v-slot="{ errors, valid }"
    :vid="(group != '' ? group + '_' : '') + vid"
    :name="($attrs.name as string | undefined) || ($attrs.label as string | undefined)"
    :rules="rulesWithoutRequiredRulesIfValidationDisabled"
    :model-value="fileIsPresent || innerValue"
    @update:model-value="updateValue"
  >
    <div>
      <b-field
        :type="{
          'is-danger': showValidationErrors && (errors[0] || customError.length > 0),
          'is-success': showValidationErrors && valid,
        }"
        :message="showValidationErrors ? [errors + customError] : ''"
        class="InputWithValidation-field"
        position="is-right"
      >
        <template v-if="$attrs.label" #label>
          <div class="fieldLabel">
            <div class="innerLabel">{{ $attrs.label }}</div>
            <div v-if="!disabled && validationActivated">
              <div v-if="isMandatory" class="mandatory">{{ $t("obligatoire") }}</div>
              <div v-else class="mandatory">{{ $t("facultatif") }}</div>
            </div>
          </div>
        </template>

        <div
          class="uploader"
          :class="{ 'has-errors': showValidationErrors && (errors[0] || customError.length > 0) }"
        >
          <div class="inputNotice mb-2">{{ inputNotice }}</div>
          <b-upload
            v-if="!disabled && !fileIsPresent && !isUploading"
            v-model="innerValue"
            class="file-label"
            accept="application/pdf"
            type="is-dark"
            @update:model-value="addFile"
          >
            <template #placeholder>
              <div>{{ placeholder }}</div>
            </template>
            <span class="file-cta">
              <span class="file-label">{{ placeholder }}</span>
            </span>
          </b-upload>
          <b-checkbox v-else v-model="fileIsPresent" style="display: none"></b-checkbox>

          <div v-if="isUploading" class="loading">
            <b-loading v-model="isUploading" :is-full-page="false" />
            {{ $t("uploading") }}
          </div>
          <div v-else-if="uploadError" class="upload-error">
            <span v-html="uploadError"></span>
          </div>
          <div v-else class="seeFile">
            <a v-if="(innerValue || fileIsPresent) && fileOperationUrl" @click="openFile">{{
              $t("see-file")
            }}</a>
            <span v-if="innerValue && !fileOperationUrl" class="file-name">
              {{ fileName }}
            </span>
            <b-button
              v-if="!disabled && (innerValue || fileIsPresent)"
              type="is-danger"
              @click="deleteFile"
            >
              <span>{{ $t("delete") }}</span>
            </b-button>
          </div>

          <b-tooltip
            v-if="tooltip && tooltip.length > 0 && !disabled"
            type="is-dark"
            :label="tooltip"
            size="is-large"
            multilined
            position="is-left"
          >
            <b-icon size="is-small" icon="question-circle"></b-icon>
          </b-tooltip>
        </div>
      </b-field>
    </div>
  </ValidationProvider>
</template>

<script lang="ts">
import Constants from "@/services/Constants"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { FileService } from "@/services/rest/utils/FileService"
import { SettingsService } from "@/services/rest/utils/SettingsService"
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import { defineComponent } from "vue"
import { BButton, BCheckbox, BField, BIcon, BLoading, BTooltip, BUpload } from "buefy"

export default defineComponent({
  name: "FileWithValidation",
  components: {
    ValidationProvider,
    BButton,
    BField,
    BIcon,
    BTooltip,
    BUpload,
    BLoading,
    BCheckbox,
  },
  props: {
    group: {
      type: String,
      default: "",
    },
    vid: {
      type: String,
      default: "",
      required: false,
    },
    rules: {
      type: [String, Array],
      default: "",
      required: false,
    },
    modelValue: {
      type: Boolean,
      required: false,
      default: false,
    },
    tooltip: {
      type: String,
      default: "",
      required: false,
    },
    inputNotice: {
      type: String,
      default: "",
      required: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    customError: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
    fileOperationUrl: {
      type: String,
      default: "",
      required: false,
    },
    defaultFileName: {
      type: String,
      default: "",
    },
  },
  emits: [
    "update:modelValue",
    "file-uploaded-or-deleted",
    "add-file",
    "manual-change",
    "delete-file",
  ],

  data() {
    return {
      infoReportingService: InfoReportingService.INSTANCE,
      fileService: FileService.INSTANCE,
      settingsService: SettingsService.INSTANCE,
      innerValue: undefined as File | undefined,
      downloadFileUrl: undefined as string | undefined,
      isUploading: false,
      uploadError: "",
      isMandatory: false,
      fileName: "",
      fileIsPresent: false,
      maxUploadSizeString: "",
      rulesWithoutRequiredRulesIfValidationDisabled: "",
    }
  },
  watch: {
    innerValue(newVal: File, _oldValue: File): void {
      this.$emit("update:modelValue", newVal)
    },
    modelValue(newVal: boolean, _oldValue: boolean): void {
      this.fileIsPresent = newVal
    },
    rules(): void {
      this.onRulesChanged()
    },
    validationActivated(): void {
      this.onRulesChanged()
    },
  },
  created(): void {
    if (this.modelValue) {
      this.fileIsPresent = this.modelValue
    }
    // Handle both string and array formats for rules in created
    if (this.rules) {
      if (Array.isArray(this.rules)) {
        this.isMandatory = this.rules.includes("required")
      } else {
        this.isMandatory = this.rules.includes("required")
      }
    } else {
      this.isMandatory = false
    }
    this.downloadFileUrl = this.fileOperationUrl
    this.onRulesChanged()
  },
  methods: {
    addFile(): void {
      if (this.innerValue) {
        this.uploadError = this.fileService.isValidFile(this.innerValue)
        if (this.uploadError !== "") {
          this.innerValue = undefined
          this.fileIsPresent = false
        } else if (this.fileOperationUrl) {
          this.isUploading = true
          this.fileService
            .uploadFileWithName(this.innerValue, this.fileOperationUrl)
            .then((_result) => {
              // File is uploaded, warnings can be refreshed
              this.$emit("file-uploaded-or-deleted", _result)
              this.$emit("add-file", this.innerValue)
              this.$emit("manual-change", this.innerValue)
              this.fileIsPresent = true
            })
            .catch(async (error) => {
              this.uploadError = this.$t("upload-failed")
              this.infoReportingService.error(errorMessage, error)
              this.fileIsPresent = false
            })
            .finally(() => {
              this.isUploading = false
            })
        } else {
          this.fileName = this.innerValue.name
        }
      }
    },

    deleteFile(): void {
      this.infoReportingService.dialog(
        this.$t("file.delete.confirmation").toString(),
        this.$t("file.delete.are-you-sure").toString(),
        this.$t("file.delete.save").toString(),
        () => this.doDeleteFile(),
        "is-warning",
      )
    },

    doDeleteFile(): void {
      if (this.fileOperationUrl) {
        this.fileService.deleteFile(this.fileOperationUrl).then((_result) => {
          // File is deleted, warnings can be refreshed
          this.$emit("file-uploaded-or-deleted", _result)
        })
      }
      this.$emit("delete-file")
      this.$emit("manual-change", undefined)
      this.innerValue = undefined
      this.fileIsPresent = false
    },

    async openFile(): Promise<void> {
      const fileURL = Constants.apiUrlv2(this.fileOperationUrl)
      window.open(fileURL, "_blank")
    },

    onRulesChanged(): void {
      // Handle both string and array formats for rules
      if (this.rules) {
        if (Array.isArray(this.rules)) {
          this.isMandatory = this.rules.includes("required")
        } else {
          // String format like "required|email"
          this.isMandatory = this.rules.includes("required")
        }
      } else {
        this.isMandatory = false
      }

      if (this.rules) {
        this.rulesWithoutRequiredRulesIfValidationDisabled = this.rules.toString()
        if (!this.validationActivated) {
          this.rulesWithoutRequiredRulesIfValidationDisabled = this.rules
            .toString()
            .replace("required|", "")
            .replace("|required", "")
            .replace("required", "")
        }
      } else {
        this.rulesWithoutRequiredRulesIfValidationDisabled = ""
      }
    },

    updateValue(newValue: any): void {
      if (typeof newValue === "boolean") {
        this.fileIsPresent = newValue
      } else {
        this.innerValue = newValue
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

.InputWithValidation-field {
  margin-bottom: 0;
}

.fieldLabel {
  display: flex;
  justify-content: space-between;
  width: 100%;

  .mandatory {
    padding-left: 20px;
    color: $dark;
  }
}

.uploader {
  display: flex;
  flex-direction: column;
  background-color: #efeeea;
  justify-content: flex-start;
  align-items: center;
  padding: 1rem;

  .inputNotice {
    width: 100%;
  }

  &.has-errors {
    border-color: $danger;
  }

  .upload-error {
    color: $danger;
    width: 100%;
    padding-top: 0.5rem;
  }

  a {
    color: currentColor;
    text-decoration: underline;
  }

  .control {
    width: 100%;
    justify-content: flex-start;
  }

  .seeFile {
    width: 100%;
    justify-content: flex-start;
    display: flex;
    align-items: center;

    a {
      padding-right: 10px;
    }
  }
}
</style>
